// Credit: https://github.com/remarkjs/react-remark/blob/main/src/index.ts#L29
// This is basically a simplified version of this but duplicated so we could
// directly leverage latest packages
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGFM from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import rehypeMdxCodeProps from 'rehype-mdx-code-props';
import rehypeRaw from 'rehype-raw';
import rehypeSantize, { defaultSchema } from 'rehype-sanitize';
import rehypeReact, { Options as RehypeReactOptions } from 'rehype-react';
import { createElement, Fragment } from 'react';
import aliasComponents, {
  AliasComponentOptions,
} from '@modules/ui/markdown/constants/aliasComponents';
import { Root } from 'rehype-stringify/lib';
import { CustomComponentNames } from '@modules/ui/markdown/constants/customComponentAliases';

export type TCompileTree = Root;

// Remember to add your custom component to @modules/ui/markdown/constants/aliasComponents
// Note: rehypeRaw will force the tagName to lowercase, so make sure you alias a lowercase
// tagName even if you use PascalCase in the markdown
const customComponentAliases = CustomComponentNames.map((name: string) => name.toLowerCase());

export const generateMarkdownProcessor = (aliasComponentOptions: AliasComponentOptions = {}) => {
  // NOTE (@mbae, 05/30/24): React 18 migration, couldn't figure out why this type was complaining
  const options: RehypeReactOptions = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    createElement,
    Fragment,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    components: aliasComponents(aliasComponentOptions),
  };

  return unified()
    .use(remarkParse)
    .use(remarkGFM)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeMdxCodeProps)
    .use(rehypeRaw, {
      passThrough: customComponentAliases,
    })
    .use(rehypeSantize, {
      ...defaultSchema,
      // Permits allowlisted custom components to be used
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tagNames: [...defaultSchema.tagNames!, ...customComponentAliases],
      attributes: {
        ...defaultSchema.attributes,
        // If your custom component has props that aren't in the default schema,
        // add them here so they won't get stripped out.

        // Note: Your component must validate the props it receives.
        // Add unit tests for your component to ensure it.

        // Custom component <colorswatch> tag has a value attribute to specify color
        colorswatch: [['value']],
        usestudiobutton: [
          ['variant', 'buttonTextTranslationKey', 'buttonProps', 'buttonStyle', 'iconStyle'],
        ],
        translation: ['keyName', 'args'],
      },
    })
    .use(rehypeReact, options);
};

const processor = generateMarkdownProcessor();

export default processor;
